import useTheme from "../../../../../common/hooks/useTheme";
import CircularGraph from "../../common/CircularGraph";
import ScoreMessageInfoPanel from "../ScoreMessageInfoPanel";
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../../store/platform-slice";
import ItemCardHome from "../ItemCardHome";
import BreakdownBarChart from "../BreakdownBarChart";
import HorizontalScrollWrapper from "../../common/HorizontalScrollWrapper";
import ItemCardHomeVerticalBar from "../ItemCardHomeVerticalBar";
import { format } from "date-fns";
import { Box } from "@mui/material";
import { useAppStore } from "../../../../../providers/AppStore";
import BlurContainer from "../../../../../common/ui/BlurContainer";
import { useAuth } from "../../../../../providers/AuthProvider";

type macronutrientItem = {
  index: number;
  label: string;
  value: number;
  maxValue: number;
  unit?: string;
  percent?: string;
};

const NutritionTab = () => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { AuthUser, AuthIsSimpleUser } = useAuth();
  const { ASSelectedClient } = useAppStore();

  const ASActiveDate = useSelector(
    (state: RootStatePlatform) => state.platform.ASActiveDate
  );

  const homeData = useSelector(
    (state: RootStatePlatform) => state.platform.dataNutServer.nutrition_tab
  );

  const getNutritionColorCodingType = (score: number) => {
    if (score === 0) {
      return 3;
    } else if (score < 60) {
      return 5;
    } else if (score >= 90) {
      return 1;
    } else {
      return 2;
    }
  };

  const normalizationOfMacroNutrientData = [
    {
      index: 0,
      label: "Carbs",
      value: homeData.carbs.current,
      maxValue: homeData.carbs.target,
      unit: "gr",
    },
    {
      index: 1,
      label: "Protein",
      value: homeData.protein.current,
      maxValue: homeData.protein.target,
      unit: "gr",
    },
    {
      index: 2,
      label: "Fat",
      value: homeData.fat.current,
      maxValue: homeData.fat.target,
      unit: "gr",
    },
  ];

  const totalCalories = normalizationOfMacroNutrientData
    .map((el: any) => (el.index === 2 ? el.value * 9 : el.value * 4))
    .reduce((sum: number, cur: number) => sum + cur, 0);

  const macronutrientsBreakdownData = normalizationOfMacroNutrientData.map(
    (macro: macronutrientItem) => {
      return {
        index: macro.index,
        label: macro.label,
        value: macro.index === 2 ? macro.value * 9 : macro.value * 4,
        unit: "Kcal",
        percentage:
          macro.index === 2
            ? 100 -
              Math.round(
                (normalizationOfMacroNutrientData[0].value * 4 * 100) /
                  totalCalories
              ) -
              Math.round(
                (normalizationOfMacroNutrientData[1].value * 4 * 100) /
                  totalCalories
              ) +
              "%"
            : Math.round((macro.value * 4 * 100) / totalCalories) + "%",
      };
    }
  );

  const normalizationOfMicroNutrientData = [
    {
      index: 1,
      label: "Cholesterol",
      value: homeData.cholesterol_milligrams.current,
      maxValue: homeData.cholesterol_milligrams.target,
      unit: "mg",
    },
    {
      index: 2,
      label: "Sugars",
      value: homeData.sugars_grams.current,
      maxValue: homeData.sugars_grams.target,
      unit: "gr",
    },
    {
      index: 3,
      label: "Saturated fat",
      value: homeData.saturated_fat_grams.current,
      maxValue: homeData.saturated_fat_grams.target,
      unit: "gr",
    },
    {
      index: 4,
      label: "Omega-3",
      value: homeData.omega3_grams.current,
      maxValue: homeData.omega3_grams.target,
      unit: "gr",
    },
    {
      index: 5,
      label: "Iron",
      value: homeData.iron_milligrams.current,
      maxValue: homeData.iron_milligrams.target,
      unit: "mg",
    },
    {
      index: 6,
      label: "Calcium",
      value: homeData.calcium_milligrams.current,
      maxValue: homeData.calcium_milligrams.target,
      unit: "mg",
    },
    {
      index: 7,
      label: "Iodine",
      value: homeData.iodine_micrograms.current,
      maxValue: homeData.iodine_micrograms.target,
      unit: "mcg",
    },
    {
      index: 8,
      label: "Sodium",
      value: homeData.sodium_milligrams.current,
      maxValue: homeData.sodium_milligrams.target,
      unit: "mg",
    },
    {
      index: 9,
      label: "Vitamin-D",
      value: homeData.vitamin_d_micrograms.current,
      maxValue: homeData.vitamin_d_micrograms.target,
      unit: "mcg",
    },
    {
      index: 10,
      label: "Potassium",
      value: homeData.potassium_milligrams.current,
      maxValue: homeData.potassium_milligrams.target,
      unit: "mg",
    },
    {
      index: 11,
      label: "Magnesium",
      value: homeData.magnesium_milligrams.current,
      maxValue: homeData.magnesium_milligrams.target,
      unit: "mg",
    },
  ];

  const normalizationOfMicroNutrientDataWithRoundValues =
    normalizationOfMicroNutrientData.map((micro: any) => {
      return {
        ...micro,
        value: Math.round(micro.value),
        maxValue: Math.round(micro.maxValue),
      };
    });

  const getSubtitleText = () => {
    if (
      AuthIsSimpleUser
        ? AuthUser.nutrition_questionnaire_completed
        : ASSelectedClient?.nutrition_questionnaire_completed
    ) {
      return AuthIsSimpleUser
        ? "Now, stay tuned for your personalized meal plan"
        : "Your client has filled the nutrition questionnaire, and their plan is now pending";
    } else {
      return AuthIsSimpleUser
        ? "And gain insights into nutrition section"
        : "Your client has not yet completed the nutrition questionnaire";
    }
  };

  const getTitleText = () => {
    if (
      AuthIsSimpleUser
        ? AuthUser.nutrition_questionnaire_completed
        : ASSelectedClient?.nutrition_questionnaire_completed
    ) {
      return AuthIsSimpleUser
        ? "Filled out the nutrition questionnaire?"
        : "Pending nutrition plan";
    } else {
      return AuthIsSimpleUser
        ? "Fill out the nutrition questionnaire"
        : "Nutrition questionnaire not completed";
    }
  };

  return (
    <div style={styles.container()}>
      <CircularGraph
        size={121}
        strokeWidth={6}
        value={homeData.score}
        color={theme.colors_ds.PRIMARY_ICON_MAIN}
        backgroundColor={theme.colorTokens.P[10]}
        label={"Nutrition"}
        isMainChart={true}
      />
      <BlurContainer
        isBlurred={
          AuthIsSimpleUser
            ? !AuthUser.has_active_plan
            : !ASSelectedClient?.has_active_plan
        }
        title={getTitleText()}
        text={getSubtitleText()}
      >
        {AuthIsSimpleUser &&
        ASActiveDate === format(new Date().toISOString(), "yyyy-MM-dd") ? (
          <ScoreMessageInfoPanel
            data={homeData.score_message}
            colorCodingType={getNutritionColorCodingType(homeData.score)}
          />
        ) : (
          <Box style={{ height: "24px" }} />
        )}
        <ItemCardHome
          label={"Calorie intake"}
          value={homeData.cals.current}
          unit="Kcal"
          maxValue={homeData.cals.target}
          hasBoldLabel={true}
        />
        <div style={styles.title(theme)}>Macronutrients</div>
        <div style={styles.cardsContainer}>
          {normalizationOfMacroNutrientData.map((el: any, index) => (
            <ItemCardHome
              key={index}
              label={el.label}
              value={el.value}
              maxValue={el.maxValue}
              unit={el.unit}
            />
          ))}
        </div>
        <BreakdownBarChart
          data={macronutrientsBreakdownData}
          type="withPercentage"
        />
        <div style={styles.micronutrientsContainer}>
          <HorizontalScrollWrapper
            title={"Micronutrients"}
            cardRefs={cardRefs}
            containerRef={containerRef}
            customMarginRight={8}
          >
            {normalizationOfMicroNutrientDataWithRoundValues.map(
              (micro: any, index: number) => (
                <ItemCardHomeVerticalBar
                  key={index}
                  item={micro}
                  cardRef={(el: any) => (cardRefs.current[index] = el)}
                />
              )
            )}
          </HorizontalScrollWrapper>
        </div>
      </BlurContainer>
    </div>
  );
};

export default NutritionTab;

const styles = {
  container: () => ({
    width: "100%",
    paddingTop: "32px",
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
  }),
  title: (theme: any) => ({
    marginTop: "16px",
    marginBottom: "16px",
    alignSelf: "flex-start",
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  micronutrientsContainer: {
    width: "100%",
    marginTop: "32px",
    marginBottom: "-32px",
  },
  cardsContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
};
